import React, { useState, useEffect, useRef } from "react";
import { message } from "@centrate-io/barn";
import { useApolloClient } from "@apollo/client";
import { IconLoader2, IconCheck, IconArrowLeft } from "@tabler/icons-react";
import { cpSendVerification, cpVerifyCode } from "_graphql/mutations";
import { usePortalSettings } from "_contexts/usePortalSettings";
import "./VerifyCode.scss";

// Background images
import FieldsImage from "_assets/images/customer-portal/fields.jpg";
import HillsImage from "_assets/images/customer-portal/hills.jpg";
import MountainsImage from "_assets/images/customer-portal/mountains.jpg";
import PoppiesImage from "_assets/images/customer-portal/poppies.jpg";
import SunsetImage from "_assets/images/customer-portal/sunset.jpg";

const backgroundImages = {
  fields: FieldsImage,
  hills: HillsImage,
  mountains: MountainsImage,
  poppies: PoppiesImage,
  sunset: SunsetImage,
};

const CODE_LENGTH = 6;
const RESEND_COUNTDOWN = 30;

function VerifyCode({ verificationData, onSuccess, onBack }) {
  const client = useApolloClient();
  const { settings } = usePortalSettings();
  const inputRefs = useRef([]);

  const [code, setCode] = useState(Array(CODE_LENGTH).fill(""));
  const [status, setStatus] = useState("idle"); // 'idle', 'sending', 'sent', 'verifying', 'expired'
  const [seconds, setSeconds] = useState(RESEND_COUNTDOWN);
  const [intervalId, setIntervalId] = useState(null);

  const { applicationId, channel, maskedDestination } = verificationData;

  // Get company settings
  const logoUrl = settings?.logo_url;
  const companyName = settings?.company?.name || "Paypath";
  const backgroundImage = settings?.background_image || "poppies";
  const bgImageUrl = backgroundImages[backgroundImage] || backgroundImages.poppies;

  // Start countdown on mount
  useEffect(() => {
    startCountdown();
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-verify when code is complete
  useEffect(() => {
    const fullCode = code.join("");
    if (fullCode.length === CODE_LENGTH && status === "idle") {
      verifyCode(fullCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const startCountdown = () => {
    if (intervalId) clearInterval(intervalId);
    setSeconds(RESEND_COUNTDOWN);

    const id = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setIntervalId(id);
  };

  const handleInputChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, "").slice(-1);

    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    // Auto-focus next input
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace") {
      if (!code[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1]?.focus();
      }
    }

    // Handle paste
    if (e.key === "v" && (e.metaKey || e.ctrlKey)) {
      return; // Let paste event handle it
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    const digits = pastedData.slice(0, CODE_LENGTH).split("");

    const newCode = [...code];
    digits.forEach((digit, i) => {
      if (i < CODE_LENGTH) {
        newCode[i] = digit;
      }
    });
    setCode(newCode);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex((d) => !d);
    const focusIndex = nextEmptyIndex === -1 ? CODE_LENGTH - 1 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const verifyCode = async (fullCode) => {
    setStatus("verifying");

    try {
      const result = await client.mutate({
        mutation: cpVerifyCode,
        variables: {
          application_id: applicationId,
          code: fullCode,
          channel: channel,
        },
      });

      const verify = result?.data?.cpVerifyCode;

      if (verify?.success && verify?.token) {
        message.success("Verification successful!");
        onSuccess(verify.token);
      } else {
        // Check if code expired
        if (verify?.error?.toLowerCase().includes("expired")) {
          setStatus("expired");
          message.error("Verification code has expired. Please request a new code.");
        } else {
          message.error(verify?.error || "Invalid code. Please try again.");
          setStatus("idle");
          // Clear code and focus first input
          setCode(Array(CODE_LENGTH).fill(""));
          inputRefs.current[0]?.focus();
        }
      }
    } catch (err) {
      console.error("Verify error:", err);
      message.error("Failed to verify code. Please try again.");
      setStatus("idle");
      setCode(Array(CODE_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    }
  };

  const resendCode = async () => {
    if (seconds > 0) return;

    setStatus("sending");

    try {
      const result = await client.mutate({
        mutation: cpSendVerification,
        variables: {
          application_id: applicationId,
          channel: channel,
        },
      });

      const send = result?.data?.cpSendVerification;

      if (send?.success) {
        setStatus("sent");
        message.success(`New code sent to ${send.masked_destination}`);

        // Reset after showing "Code sent!" briefly
        setTimeout(() => {
          setStatus("idle");
          setCode(Array(CODE_LENGTH).fill(""));
          startCountdown();
          inputRefs.current[0]?.focus();
        }, 1500);
      } else {
        message.error(send?.error || "Failed to send code. Please try again.");
        setStatus("idle");
      }
    } catch (err) {
      console.error("Resend error:", err);
      message.error("Failed to send code. Please try again.");
      setStatus("idle");
    }
  };

  const renderStatusIndicator = () => {
    if (status === "sending") {
      return (
        <div className="status-indicator sending">
          <IconLoader2 className="status-icon spinning" size={18} />
          <span>Sending code...</span>
        </div>
      );
    }

    if (status === "sent") {
      return (
        <div className="status-indicator sent">
          <IconCheck className="status-icon" size={16} />
          <span>Code sent!</span>
        </div>
      );
    }

    if (status === "verifying") {
      return (
        <div className="status-indicator verifying">
          <IconLoader2 className="status-icon spinning" size={18} />
          <span>Validating code...</span>
        </div>
      );
    }

    if (status === "expired") {
      return (
        <button className="request-new-code-button" onClick={resendCode}>
          Request a new code
        </button>
      );
    }

    // Default: show countdown or resend button
    if (seconds > 0) {
      return (
        <div className="status-indicator countdown">
          <IconLoader2 className="status-icon" size={18} />
          <span>Request new code in {seconds} seconds</span>
        </div>
      );
    }

    return (
      <button className="resend-link" onClick={resendCode}>
        Request a new verification code
      </button>
    );
  };

  // Render expired state
  if (status === "expired") {
    return (
      <div className="auth-verify-container">
        <div
          className="auth-background"
          style={{ backgroundImage: `url(${bgImageUrl})` }}
        />

        <div className="auth-modal expired">
          <div className="auth-content">
            {logoUrl && (
              <div className="brand-icon">
                <img src={logoUrl} alt={companyName} />
              </div>
            )}

            <div className="verify-section">
              <h1 className="verify-title">Request A New Verification Code</h1>
              <p className="verify-subtitle">Verification code expired.</p>
            </div>

            <button className="request-new-code-button" onClick={resendCode}>
              Request a new code
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render sending/sent state (verification in progress)
  if (status === "sending" || status === "sent") {
    return (
      <div className="auth-verify-container">
        <div
          className="auth-background"
          style={{ backgroundImage: `url(${bgImageUrl})` }}
        />

        <div className="auth-modal sending-state">
          <div className="auth-content">
            {logoUrl && (
              <div className="brand-icon">
                <img src={logoUrl} alt={companyName} />
              </div>
            )}

            <div className="verify-section">
              <h1 className="verify-title">Verifying your identity...</h1>
              <p className="verify-subtitle">
                We're sending a one-time code to {maskedDestination} to confirm
                it's really you.
              </p>
            </div>

            {renderStatusIndicator()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-verify-container">
      <div
        className="auth-background"
        style={{ backgroundImage: `url(${bgImageUrl})` }}
      />

      <div className="auth-modal">
        {/* Back button - hidden on mobile via CSS */}
        <button className="back-button" onClick={onBack}>
          <IconArrowLeft size={20} />
        </button>

        <div className="auth-content">
          {logoUrl && (
            <div className="brand-icon">
              <img src={logoUrl} alt={companyName} />
            </div>
          )}

          <div className="verify-section">
            <h1 className="verify-title">Enter Verification Code</h1>
            <p className="verify-subtitle">
              A verification code was sent to{" "}
              <strong>{maskedDestination}</strong>
            </p>
          </div>

          {/* OTP Input Boxes */}
          <div className="otp-input-container">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={`otp-input ${digit ? "filled" : ""}`}
                disabled={status === "verifying"}
                autoFocus={index === 0}
              />
            ))}
          </div>

          {renderStatusIndicator()}
        </div>
      </div>
    </div>
  );
}

export default VerifyCode;
